import type { Waga } from './zegar-agenta';

/**
 * BAZA, NA KTÓREJ PRACUJE MASZYNA, WOBEC BAZY MASTER WYSPY.
 *
 * ⚠️ SAMA NAZWA BAZY NIC NIE ZNACZY — dokładnie jak strefa czasowa w `zegar-agenta.ts`. `pesa`
 * jest poprawna na wyspie, która ma `pesa` za mastera, i jest awarią na wyspie, która ma
 * `temrex_sdi`. Znaczenie ma wyłącznie porównanie, więc ekran dostaje obie wartości naraz.
 *
 * ⚠️ ISTNIEJE PO REALNEJ SYTUACJI (małpkomat, 2026-08-24): SupplyPointa przepięto na bazę `pesa`,
 * agent został przy `temrex_sdi` i ciągnął ją dziesięć dni. Portal pokazywał `temrex_sdi` jako
 * nazwę bazy agenta i wyglądało to poprawnie — bo nie było obok czego, z czym to zderzyć.
 */
export type OpisBazy = { tekst: string; waga: Waga; powod: string | null };

const rowna = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * CO WIEMY O WĘŹLE POZA NAZWĄ JEGO BAZY — bez tego porównanie odpowiada na złe pytanie.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ DOŁOŻONE PO ZGŁOSZENIU Z WDROŻENIA PESA (28.08.2026). Master wyspy stanął w chmurze pod
 *  nazwą `pesa_master`, a maszyny mają lokalnie `mjm` — czyli DOKŁADNIE to, na czym stoi model
 *  Smart Synca. Ekran zapalił wtedy ostrzeżenie „mjm ≠ pesa_master" przy KAŻDEJ z trzech maszyn.
 *
 *  ⚠️ TO JEST TA SAMA POMYŁKA, KTÓRĄ REGUŁA ALERTÓW (`api/.../rozjazd-bazy.ts`) ma opisaną
 *  i zamkniętą od dawna: „bez rozróżnienia trybu pierwsza wyspa przełączona w Smart Sync zapaliłaby
 *  alert na KAŻDEJ maszynie naraz — czyli dokładnie ten szum, po którym alertów przestaje się
 *  czytać". Alerty tego nie robiły, a EKRAN robił, bo miał własną regułę i nikt jej o trybie
 *  nie powiedział. Sprawdzenie jednej z tych dwóch dróg i uznanie, że druga zachowuje się tak
 *  samo, było błędem.
 *
 *  ⚠️ ODWROTNIE NIŻ MOŻNA BY SĄDZIĆ, PORÓWNANIE NIE ZNIKA — ono się PRZENOSI. W Smart Syncu jest
 *  dokładnie jeden węzeł, dla którego nazwa bazy MUSI zgadzać się z masterem wyspy: ten, który
 *  jest wyłącznie bazą (chmurowy sidecar, „baza bez automatu"). I to właśnie tam rozjazd był
 *  prawdziwym problemem — sidecar szukał `mjm`, którego nie ma, więc nie widział odtworzonej
 *  bazy. Stara reguła zasypywała ten jeden ważny wiersz trzema nieprawdziwymi.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type KontekstWezla = {
  /** Tryb wyspy. Brak czytamy jako `ZWYKLY` — tak zachowywał się ekran przed wprowadzeniem trybów. */
  tryb?: string | null;
  /**
   * Czy ten węzeł jest WYŁĄCZNIE bazą (brak dowiązanego automatu).
   *
   * Ta sama definicja, którą po stronie API rozstrzyga `czyToZasianieMastera`: master wyspy nie
   * ma własnego automatu, bo jest tylko bazą.
   */
  tylkoBaza?: boolean;
};

export function opisBazy(
  bazaWezla: string | null | undefined,
  bazaWyspy: string | null | undefined,
  kontekst: KontekstWezla = {},
): OpisBazy {
  if (bazaWezla === null || bazaWezla === undefined || bazaWezla === '') {
    return { tekst: 'nie podano', waga: 'nieznane', powod: null };
  }
  if (bazaWyspy === null || bazaWyspy === undefined || bazaWyspy === '') {
    /** Wyspa bez mastera — pierwsza rejestracja go dopiero ustala, więc nie ma z czym porównać. */
    return { tekst: bazaWezla, waga: 'ok', powod: null };
  }

  /**
   * ⚠️ W SMART SYNCU MASZYNA Z WŁASNĄ BAZĄ TO STAN NORMALNY, NIE USTERKA — patrz `KontekstWezla`.
   * Milczymy więc o niej całkiem, zamiast ostrzegać „na wszelki wypadek": ostrzeżenie zapalone
   * przy każdej maszynie wyspy nie odróżnia niczego od niczego.
   */
  if (kontekst.tryb === 'SMART_SYNC' && kontekst.tylkoBaza !== true) {
    return { tekst: bazaWezla, waga: 'ok', powod: null };
  }
  /**
   * ⚠️ Porównanie NIEWRAŻLIWE na wielkość liter: kolacja SPS jest CI, a nazwy baz w plikach
   * konfiguracyjnych bywają pisane różnie. Ostrzeżenie o samej wielkości liter byłoby fałszywe.
   */
  if (!rowna(bazaWezla, bazaWyspy)) {
    /**
     * ⚠️ WĘZEŁ, KTÓRY JEST TYLKO BAZĄ, DOSTAJE INNE ZDANIE — bo u niego rozjazd nazwy ma inny
     * skutek i inną naprawę. Nie „portal pokazuje dane z innej bazy", a „ten węzeł nie zobaczy
     * bazy, którą odtworzono": agent łączy się do nazwy z `SPS_DB`, więc dopóki się nie zgadzają,
     * melduje „bazy jeszcze nie ma", choć baza mastera stoi obok gotowa. Zmierzone na PESA.
     */
    if (kontekst.tylkoBaza === true) {
      return {
        tekst: bazaWezla,
        waga: 'uwaga',
        powod:
          `master wyspy to „${bazaWyspy}", a ten węzeł pracuje na „${bazaWezla}" — dopóki nazwy się ` +
          'nie zgadzają, nie zobaczy odtworzonej bazy mastera (zmień `SPS_DB` w konfiguracji tego węzła)',
      };
    }
    return {
      tekst: bazaWezla,
      waga: 'uwaga',
      powod:
        `wyspa ma za mastera „${bazaWyspy}" — portal pokazuje dane z tamtej bazy, ` +
        'a ta maszyna pracuje na innej',
    };
  }
  return { tekst: bazaWezla, waga: 'ok', powod: null };
}
